import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SiteLayout from '../layout/SiteLayout';
import Seo from '../seo/Seo';
import AppCta from './AppCta';
import {
  Breadcrumbs,
  DirectAnswer,
  DisclaimerNote,
  FaqSection,
  RelatedLinks,
  ReviewedBy,
  SourcesList,
} from './ContentBlocks';
import { articleJsonLd, breadcrumbJsonLd, faqJsonLd, howToCutJsonLd, itemListJsonLd, recipeJsonLd, webPageJsonLd } from '../../lib/jsonld';
import { getFoodBySlug, getGuideBySlug, getRecipeBySlug } from '../../content';
import { useContentLang, useLocalizedItem, useLocalizedPath } from '../../hooks/useLocalizedContent';
import { relatedItems } from './FoodPage';
import './Knowledge.css';

function renderParagraphs(text) {
  if (Array.isArray(text)) {
    return text.map((paragraph) => <p key={paragraph.slice(0, 40)}>{paragraph}</p>);
  }
  return text.split('\n\n').map((paragraph) => <p key={paragraph.slice(0, 40)}>{paragraph}</p>);
}

export function GuidePage({ guide: guideProp }) {
  const { t } = useTranslation();
  const lang = useContentLang();
  const guide = useLocalizedItem(guideProp);
  const toPath = useLocalizedPath();
  const pagePath = toPath(guide.path);

  const breadcrumbs = [
    { name: t('knowledge.page.home'), path: '/' },
    { name: t('knowledge.page.guides'), path: toPath('/guides') },
    { name: guide.title, path: pagePath },
  ];

  const relatedFoods = relatedItems(guide.relatedFoods, getFoodBySlug, 'name', lang, toPath);
  const relatedGuides = relatedItems(guide.relatedGuides, getGuideBySlug, 'title', lang, toPath);
  const relatedRecipes = relatedItems(guide.relatedRecipes, getRecipeBySlug, 'title', lang, toPath);

  return (
    <SiteLayout>
      <Seo
        title={guide.title}
        description={guide.metaDescription || guide.shortAnswer}
        path={pagePath}
        lang={lang}
        jsonLd={[
          breadcrumbJsonLd(breadcrumbs),
          articleJsonLd({
            title: guide.title,
            description: guide.shortAnswer,
            path: pagePath,
            datePublished: guide.createdAt,
            dateModified: guide.reviewedAt,
            authorName: guide.reviewedBy?.name,
            inLanguage: lang,
          }),
          faqJsonLd(guide.faqs),
        ]}
      />
      <article className="kb-page">
        <Breadcrumbs items={breadcrumbs} />
        <p className="kb-kicker">{t('knowledge.page.guideKicker')}</p>
        <h1>{guide.h1 || guide.title}</h1>
        <DirectAnswer>{guide.shortAnswer}</DirectAnswer>
        <DisclaimerNote />
        {guide.sections.map((section) => (
          <section className="kb-section" key={section.heading}>
            <h2>{section.heading}</h2>
            {section.lead ? <p className="kb-lead">{section.lead}</p> : null}
            {section.body ? renderParagraphs(section.body) : null}
            {section.bullets ? (
              <ul>
                {section.bullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
        <FaqSection faqs={guide.faqs} />
        <SourcesList sources={guide.sources} />
        <ReviewedBy reviewer={guide.reviewedBy} reviewedAt={guide.reviewedAt} lang={lang} />
        <RelatedLinks title={t('knowledge.page.relatedFoods')} items={relatedFoods} />
        <RelatedLinks title={t('knowledge.page.relatedGuides')} items={relatedGuides} />
        <RelatedLinks title={t('knowledge.page.relatedRecipes')} items={relatedRecipes} />
        <AppCta />
      </article>
    </SiteLayout>
  );
}

function groupCutsByFood(cuts = []) {
  const groups = [];
  cuts.forEach((cut) => {
    const last = groups[groups.length - 1];
    if (last && last.foodName === cut.foodName) {
      last.cuts.push(cut);
    } else {
      groups.push({ foodName: cut.foodName, foodSlug: cut.foodSlug, cuts: [cut] });
    }
  });
  return groups;
}

function recipeSearchFaqs(recipe, t) {
  const extras = [
    {
      question: t('knowledge.page.whenCanEatRecipe', { title: recipe.title, count: recipe.minAgeMonths }),
      answer: t('knowledge.page.whenCanEatRecipeAnswer', {
        title: recipe.title,
        count: recipe.minAgeMonths,
      }),
    },
    {
      question: t('knowledge.page.howToServeThis', { title: recipe.title }),
      answer: t('knowledge.page.howToServeThisAnswer', {
        title: recipe.title,
        count: recipe.minAgeMonths,
      }),
    },
  ];
  const existing = new Set((recipe.faqs || []).map((faq) => faq.question.toLowerCase()));
  return [...(recipe.faqs || []), ...extras.filter((faq) => !existing.has(faq.question.toLowerCase()))];
}

export function RecipePage({ recipe: recipeProp }) {
  const { t } = useTranslation();
  const lang = useContentLang();
  const recipe = useLocalizedItem(recipeProp);
  const toPath = useLocalizedPath();
  const pagePath = toPath(recipe.path);
  const cutGroups = groupCutsByFood(recipe.cutExamples);
  const faqs = recipeSearchFaqs(recipe, t);

  const breadcrumbs = [
    { name: t('knowledge.page.home'), path: '/' },
    { name: t('knowledge.page.recipes'), path: toPath('/recipes') },
    { name: recipe.title, path: pagePath },
  ];
  const relatedFoods = relatedItems(recipe.relatedFoods, getFoodBySlug, 'name', lang, toPath);
  const seoTitle = t('knowledge.page.recipeSeoTitle', { title: recipe.title });

  return (
    <SiteLayout>
      <Seo
        title={seoTitle}
        description={recipe.metaDescription || recipe.shortAnswer}
        path={pagePath}
        image={recipe.imageUrl}
        lang={lang}
        jsonLd={[
          webPageJsonLd({
            title: seoTitle,
            description: recipe.shortAnswer,
            path: pagePath,
            inLanguage: lang,
          }),
          breadcrumbJsonLd(breadcrumbs),
          recipeJsonLd({
            title: recipe.title,
            description: recipe.shortAnswer,
            path: pagePath,
            image: recipe.imageUrl,
            images: (recipe.cutExamples || []).map((cut) => cut.imageUrl),
            datePublished: recipe.createdAt,
            dateModified: recipe.reviewedAt,
            authorName: recipe.reviewedBy?.name,
            ingredients: recipe.ingredients,
            steps: recipe.steps,
            prepTimeMinutes: recipe.prepTimeMinutes,
            minAgeMonths: recipe.minAgeMonths,
            inLanguage: lang,
          }),
          howToCutJsonLd({
            title: recipe.title,
            path: pagePath,
            cuts: recipe.cutExamples,
            inLanguage: lang,
          }),
          faqJsonLd(faqs),
        ]}
      />
      <article className="kb-page">
        <Breadcrumbs items={breadcrumbs} />
        {recipe.imageUrl ? (
          <img
            className="kb-hero-image kb-hero-image-wide"
            src={recipe.imageUrl}
            alt={t('knowledge.page.recipeHeroAlt', { title: recipe.title })}
          />
        ) : null}
        <p className="kb-kicker">{t('knowledge.page.recipeKicker')}</p>
        <h1>{recipe.h1 || recipe.title}</h1>
        <p className="kb-meta">
          {t('knowledge.page.fromMinutes', {
            count: recipe.minAgeMonths,
            minutes: recipe.prepTimeMinutes,
          })}
          {recipe.allergens?.length
            ? ` · ${t('knowledge.page.allergensLabel', { list: recipe.allergens.join(', ') })}`
            : ''}
        </p>
        <DirectAnswer>{recipe.shortAnswer}</DirectAnswer>
        <DisclaimerNote />
        <section className="kb-section">
          <h2>{t('knowledge.page.whenCanEatRecipe', { title: recipe.title, count: recipe.minAgeMonths })}</h2>
          <p className="kb-lead">
            {t('knowledge.page.whenCanEatRecipeAnswer', {
              title: recipe.title,
              count: recipe.minAgeMonths,
            })}
          </p>
        </section>
        <section className="kb-section">
          <h2>{t('knowledge.page.howToServeThis', { title: recipe.title })}</h2>
          <p className="kb-lead">
            {t('knowledge.page.howToServeThisAnswer', {
              title: recipe.title,
              count: recipe.minAgeMonths,
            })}
          </p>
        </section>
        <section className="kb-section">
          <h2>{t('knowledge.page.ingredients')}</h2>
          <ul>
            {recipe.ingredients.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section className="kb-section">
          <h2>{t('knowledge.page.steps')}</h2>
          <ol>
            {recipe.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>
        {cutGroups.length ? (
          <section className="kb-section" id="how-to-cut">
            <h2>{t('knowledge.page.howToCut')}</h2>
            <p className="kb-lead">{t('knowledge.page.howToCutLead')}</p>
            {cutGroups.map((group) => (
              <div className="cut-group" key={group.foodName}>
                <h3>
                  {group.foodSlug ? (
                    <Link to={toPath(`/foods/${group.foodSlug}`)}>
                      {t('knowledge.page.howToCutFood', { name: group.foodName })}
                    </Link>
                  ) : (
                    t('knowledge.page.howToCutFood', { name: group.foodName })
                  )}
                </h3>
                <div className="serving-grid">
                  {group.cuts.map((cut) => {
                    const ageLabel =
                      cut.maxAgeMonths >= 24
                        ? t('knowledge.page.ageFrom', { count: cut.minAgeMonths })
                        : t('knowledge.page.ageRange', { min: cut.minAgeMonths, max: cut.maxAgeMonths });
                    return (
                      <figure key={`${cut.foodName}-${cut.minAgeMonths}-${cut.cutType}`} className="serving-card">
                        {cut.imageUrl ? (
                          <img
                            src={cut.imageUrl}
                            alt={t('knowledge.page.cutAlt', { name: cut.foodName, age: ageLabel })}
                          />
                        ) : null}
                        <figcaption>
                          <h3>{t('knowledge.page.cutAtAge', { name: cut.foodName, age: ageLabel })}</h3>
                          <p>{cut.description}</p>
                        </figcaption>
                      </figure>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>
        ) : null}
        {recipe.servingNotes ? (
          <section className="kb-section">
            <h2>{t('knowledge.page.howToServeRecipe')}</h2>
            {renderParagraphs(recipe.servingNotes)}
          </section>
        ) : null}
        <FaqSection faqs={faqs} />
        <SourcesList sources={recipe.sources} />
        <ReviewedBy reviewer={recipe.reviewedBy} reviewedAt={recipe.reviewedAt} lang={lang} />
        <RelatedLinks title={t('knowledge.page.foodsInRecipe')} items={relatedFoods} />
        <AppCta text={t('knowledge.page.recipeCtaText')} />
      </article>
    </SiteLayout>
  );
}

export function MealPlanPage({ plan: planProp }) {
  const { t } = useTranslation();
  const lang = useContentLang();
  const plan = useLocalizedItem(planProp);
  const toPath = useLocalizedPath();
  const pagePath = toPath(plan.path);

  const breadcrumbs = [
    { name: t('knowledge.page.home'), path: '/' },
    { name: t('knowledge.page.mealPlans'), path: toPath('/meal-plans') },
    { name: plan.title, path: pagePath },
  ];

  return (
    <SiteLayout>
      <Seo
        title={plan.title}
        description={plan.metaDescription || plan.shortAnswer}
        path={pagePath}
        lang={lang}
        jsonLd={[
          breadcrumbJsonLd(breadcrumbs),
          articleJsonLd({
            title: plan.title,
            description: plan.shortAnswer,
            path: pagePath,
            datePublished: plan.createdAt,
            dateModified: plan.reviewedAt,
            authorName: plan.reviewedBy?.name,
            inLanguage: lang,
          }),
          faqJsonLd(plan.faqs),
        ]}
      />
      <article className="kb-page">
        <Breadcrumbs items={breadcrumbs} />
        <p className="kb-kicker">{t('knowledge.page.planKicker')}</p>
        <h1>{plan.h1 || plan.title}</h1>
        <DirectAnswer>{plan.shortAnswer}</DirectAnswer>
        <DisclaimerNote />
        {plan.sections.map((section) => (
          <section className="kb-section" key={section.heading}>
            <h2>{section.heading}</h2>
            {section.lead ? <p className="kb-lead">{section.lead}</p> : null}
            {section.body ? renderParagraphs(section.body) : null}
            {section.meals ? (
              <div className="meal-day-grid">
                {section.meals.map((meal) => (
                  <div className="meal-day-card" key={meal.label}>
                    <h3>{meal.label}</h3>
                    <ul>
                      {meal.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}
            {section.bullets ? (
              <ul>
                {section.bullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
        <FaqSection faqs={plan.faqs} />
        <SourcesList sources={plan.sources} />
        <ReviewedBy reviewer={plan.reviewedBy} reviewedAt={plan.reviewedAt} lang={lang} />
        <AppCta
          title={t('knowledge.page.planCtaTitle', { age: plan.ageLabel })}
          text={t('knowledge.page.planCtaText')}
        />
      </article>
    </SiteLayout>
  );
}

export function HubPage({
  title,
  description,
  path,
  kicker,
  items,
  itemLabel = 'Read',
}) {
  const { t } = useTranslation();
  const lang = useContentLang();
  const toPath = useLocalizedPath();
  const pagePath = toPath(path);
  const localizedItems = items.map((item) => ({
    ...item,
    path: toPath(item.path),
  }));

  const breadcrumbs = [
    { name: t('knowledge.page.home'), path: '/' },
    { name: title, path: pagePath },
  ];

  return (
    <SiteLayout>
      <Seo
        title={title}
        description={description}
        path={pagePath}
        lang={lang}
        type="website"
        jsonLd={[breadcrumbJsonLd(breadcrumbs), itemListJsonLd(title, localizedItems)]}
      />
      <div className="kb-page">
        <Breadcrumbs items={breadcrumbs} />
        <p className="kb-kicker">{kicker}</p>
        <h1>{title}</h1>
        <DirectAnswer>{description}</DirectAnswer>
        <div className="hub-grid">
          {localizedItems.map((item) => (
            <Link className="hub-card" to={item.path} key={item.path}>
              {item.imageUrl ? <img src={item.imageUrl} alt="" /> : null}
              <div>
                <h2>{item.name || item.title}</h2>
                <p>{item.shortAnswer || item.summary}</p>
                <span>{itemLabel}</span>
              </div>
            </Link>
          ))}
        </div>
        <AppCta />
      </div>
    </SiteLayout>
  );
}
